import { ActivatedRouteSnapshot, CanActivate, UrlTree } from '@angular/router';
import { Injectable } from '@angular/core';
import { NavController, Platform } from '@ionic/angular';
import { AppConfigService, CompanyGroupFacade } from '@symphony-solo/domain/company-group/src';
import { catchError, first, map } from 'rxjs/operators';
import { CompanyConfigFacade } from '@symphony-solo/domain/company-config/src/lib/+state/company-config.facade';
import { ConnectivityFacade, ConnectivityStatusEnum } from '@symphony-solo/shared/network-connectivity/src';
import { ScanQROrderFacade } from '@symphony-solo/domain/scan-qr-order/src';
import { AuthenticationFacade } from '../+state';
import { combineLatest, Observable, of } from 'rxjs';

@Injectable()
export class CompanyGroupGuard implements CanActivate {
  public currentRoute: string;
  private companyGroupPayload: any = {
    groupGuid: 'D1590806-1938-482A-B88C-4BF0FD742677',
    appID: '',
  };

  constructor(
    private navCtrl: NavController,
    private companyGroupFacade: CompanyGroupFacade,
    private companyConfigFacade: CompanyConfigFacade,
    private authenticationFacade: AuthenticationFacade,
    private appConfigService: AppConfigService,
    private scanQROrderFacade: ScanQROrderFacade,
    private platform: Platform,
    private connectivityFacade: ConnectivityFacade
  ) {}

  public canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return combineLatest([
      this.authenticationFacade.isAuthenticated$,
      this.companyGroupFacade.companyGroupInfo$,
      this.scanQROrderFacade.isOrderFromSite$,
      this.scanQROrderFacade.siteId$,
      this.connectivityFacade.isWifiConnected$,
    ]).pipe(
      first(),
      map(([isAuthenticated, companyGroupInfo, isOrderFromSite, siteId, isWifiConnected]) => {
        const isScanQrParamsAvaliable = Object.keys(route?.queryParams).length > 0;
        if (isWifiConnected === ConnectivityStatusEnum.Connected) {
          if (companyGroupInfo) {
            this.appConfigService.setConfig(companyGroupInfo);
            this.companyConfigFacade.getCompanyConfig();

            if (isScanQrParamsAvaliable) {
              this.processQrScan(route);
            } else if (isAuthenticated) {
              this.navCtrl.navigateForward('/app');
            } else {
              if (isOrderFromSite) {
                this.scanQROrderFacade.getChooseLocation(siteId);
              } else {
                this.navCtrl.navigateRoot(['/login']);
              }
            }
          } else {
            const isWeb = !this.platform.is('capacitor') && !this.platform.is('cordova');
            if (isWeb) {
              if (isScanQrParamsAvaliable) {
                this.processQrScan(route);
              }
              this.companyGroupFacade.getCompanyGroupBySubDomain();
            } else {
              if (isScanQrParamsAvaliable && route?.queryParams.show) {
                return true;
              } else {
                this.companyGroupFacade.getCompanyGroup(this.companyGroupPayload);
              }
            }
          }
        }

        return false;
      }),
      catchError((err) => {
        return of(false);
      })
    );
  }

  private processQrScan(route) {
    if (Object.keys(route?.queryParams).length > 0) {
      const params = route.queryParams;
      const siteId = this.formatSiteId(params.sid);
      this.companyConfigFacade.setSiteID(siteId);
      this.companyConfigFacade.setOrderFromSite(params.orderfromsite);
      this.scanQROrderFacade.setSiteID(siteId);
      this.scanQROrderFacade.setOrderFromSite(params.orderfromsite);
      this.scanQROrderFacade.getChooseLocation(siteId);

      this.scanQROrderFacade.clearState('childDeliveryLocation');
      this.scanQROrderFacade.clearState('deliveryLocation');
    }
  }

  private formatSiteId(siteId): string {
    return siteId.split('_').join('/');
  }
}
